import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import Disclosure from '../common/Disclosure'
import ResponsiveImage from '../common/ResponsiveImage'
import { BOOKING_URL, business } from '../../data/site'
import { teamBySlug } from '../../data/team'

export const fadeUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-60px' },
  transition: { duration: 0.6, ease: 'easeOut' },
}

export const Breadcrumbs = ({ items }) => (
  <nav aria-label="Breadcrumb" className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-8">
    <ol className="flex flex-wrap items-center gap-2 font-sans text-xs tracking-[0.1em] uppercase text-dark-400">
      {items.map((item, i) => (
        <li key={item.path} className="flex items-center gap-2">
          {i < items.length - 1 ? (
            <>
              <Link to={item.path} className="hover:text-brand-orange-500 transition-colors">
                {item.name}
              </Link>
              <span aria-hidden="true">/</span>
            </>
          ) : (
            <span aria-current="page" className="text-dark-600">
              {item.name}
            </span>
          )}
        </li>
      ))}
    </ol>
  </nav>
)

export const BookButton = ({ label = 'Book an Appointment', className = '' }) => (
  <a
    href={BOOKING_URL}
    target="_blank"
    rel="noopener noreferrer"
    className={`inline-block bg-brand-orange-500 text-white px-8 py-3 rounded-full font-sans text-[11px] font-medium tracking-[0.15em] uppercase hover:bg-brand-orange-400 transition-colors duration-200 shadow-sm ${className}`}
  >
    {label}
  </a>
)

export const ProseSections = ({ sections }) => (
  <div className="space-y-10">
    {sections.map((section) => (
      <motion.section key={section.heading} {...fadeUp}>
        <h2 className="font-display text-dark-700 text-2xl md:text-3xl font-semibold leading-tight mb-4">
          {section.heading}
        </h2>
        {section.body.map((p, i) => (
          <p key={i} className="font-sans text-dark-500 text-base md:text-lg leading-relaxed mb-4">
            {p}
          </p>
        ))}
      </motion.section>
    ))}
  </div>
)

export const BulletList = ({ items, dot = 'bg-brand-orange-500' }) => (
  <ul className="space-y-2.5">
    {items.map((item) => {
      const isObject = typeof item === 'object'
      const key = isObject ? item.label : item
      return (
        <li key={key} className="flex items-start gap-3 font-sans text-dark-500 text-base leading-relaxed">
          <span className={`w-1.5 h-1.5 rounded-full ${dot} mt-2 flex-shrink-0`} />
          {isObject ? (
            <span>
              <span className="font-medium text-dark-700">{item.label}</span>
              {' — '}
              {item.detail}
            </span>
          ) : (
            item
          )}
        </li>
      )
    })}
  </ul>
)

export const PricingList = ({ pricing }) => (
  <ul className="divide-y divide-dark-200/40">
    {pricing.map((item) => (
      <li key={item.duration} className="flex justify-between items-baseline gap-4 py-3">
        <span className="font-sans text-dark-600 text-base">{item.duration}</span>
        <span className="font-display text-dark-700 text-lg font-medium flex-shrink-0">
          {item.price}
        </span>
      </li>
    ))}
  </ul>
)

export const SideCard = ({ children, className = '' }) => (
  <div
    className={`${className.includes('bg-') ? '' : 'bg-white'} rounded-[20px] border border-dark-200/25 shadow-sm p-6 md:p-8 ${className}`}
  >
    {children}
  </div>
)

export const SectionLabel = ({ children }) => (
  <span className="font-script text-brand-orange-500 italic text-lg block mb-3">{children}</span>
)

export const PractitionerGrid = ({ slugs, heading = 'Your practitioners' }) => {
  const members = slugs.map(teamBySlug).filter(Boolean)
  if (!members.length) return null
  return (
    <section className="bg-cream-100 py-16 md:py-20">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div {...fadeUp}>
          <SectionLabel>Meet the team</SectionLabel>
          <h2 className="font-display text-dark-700 text-3xl md:text-4xl font-semibold mb-10">
            {heading}
          </h2>
        </motion.div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {members.map((m) => (
            <Link
              key={m.slug}
              to={`/team/${m.slug}`}
              className="group bg-white rounded-2xl border border-dark-200/25 p-6 shadow-sm hover:shadow-md transition-shadow flex flex-col items-center text-center"
            >
              <div className="w-24 h-24 rounded-full overflow-hidden border-[3px] border-cream-200 shadow mb-4">
                <ResponsiveImage
                  src={m.image}
                  alt={`${m.name}, ${m.title}`}
                  sizes="96px"
                  className="w-full h-full object-cover object-[center_18%]"
                />
              </div>
              <h3 className="font-display text-dark-700 text-lg font-medium group-hover:text-brand-orange-500 transition-colors">
                {m.name}
              </h3>
              <p className="font-sans text-dark-400 text-xs tracking-[0.1em] uppercase mt-1">
                {m.fullTitle}
              </p>
            </Link>
          ))}
        </div>
      </div>
    </section>
  )
}

export const FaqSection = ({ faqs, heading = 'Frequently asked questions' }) => {
  if (!faqs?.length) return null
  return (
    <section className="bg-cream-200 py-16 md:py-20">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div {...fadeUp}>
          <SectionLabel>Questions</SectionLabel>
          <h2 className="font-display text-dark-700 text-3xl md:text-4xl font-semibold mb-8">
            {heading}
          </h2>
        </motion.div>
        <div className="border-b border-dark-300/40">
          {faqs.map((f) => (
            <Disclosure key={f.q} title={f.q} as="h3">
              <p className="font-sans text-dark-500 text-base leading-relaxed">{f.a}</p>
            </Disclosure>
          ))}
        </div>
      </div>
    </section>
  )
}

export const LinkCards = ({ heading, label, items, bg = 'bg-cream-100' }) => {
  if (!items.length) return null
  return (
    <section className={`${bg} py-16 md:py-20`}>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div {...fadeUp}>
          {label && <SectionLabel>{label}</SectionLabel>}
          <h2 className="font-display text-dark-700 text-3xl md:text-4xl font-semibold mb-10">
            {heading}
          </h2>
        </motion.div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {items.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              className="group bg-white rounded-[20px] overflow-hidden border border-dark-200/25 shadow-sm hover:shadow-md transition-shadow flex flex-col"
            >
              {item.image && (
                <div className="aspect-[4/3] overflow-hidden">
                  <ResponsiveImage
                    src={item.image}
                    alt={item.imageAlt || item.name}
                    sizes="(min-width: 1024px) 360px, (min-width: 640px) 50vw, 100vw"
                    className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500"
                  />
                </div>
              )}
              <div className="p-6 md:p-7 flex flex-col flex-grow">
                <h3 className="font-display text-dark-700 text-xl font-semibold mb-2 group-hover:text-brand-orange-500 transition-colors">
                  {item.name}
                </h3>
                {item.description && (
                  <p className="font-sans text-dark-500 text-base leading-relaxed mb-4">
                    {item.description}
                  </p>
                )}
                <span className="mt-auto font-sans text-xs tracking-[0.15em] uppercase text-brand-orange-600">
                  Learn more →
                </span>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  )
}

export const BookingBand = ({ heading = 'Ready to feel better in your body?', label }) => (
  <section className="bg-dark-700 py-16 md:py-20">
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
      <h2 className="font-display text-cream-100 text-3xl md:text-4xl font-semibold mb-4">
        {heading}
      </h2>
      <p className="font-sans text-cream-200/80 text-base md:text-lg leading-relaxed mb-8">
        {business.street} · Yonge &amp; Eglinton, Midtown Toronto. Book online any time, or
        call{' '}
        <a href={business.phoneHref} className="underline underline-offset-4">
          {business.phoneDisplay}
        </a>
        .
      </p>
      <BookButton label={label} />
    </div>
  </section>
)
